/* eslint-disable no-shadow */
import { SerializedError } from '@reduxjs/toolkit';
import { createApi } from '@reduxjs/toolkit/query/react';
import B2ChatClient, {
  B2ChatClientBroadcast,
  apiResource,
  b2chatBaseQuery,
  isQueryErrorResult,
} from '@src/client';
import dayjs from '@utils/dayjs';
import isPublicRoute from '@utils/isPublicRoute';
import {
  AI_CONVERSATIONS_FIRST_DAY,
  ISO_DAY_FORMAT,
} from '@components/AdminHome/Analytics/AIConversations/constants';

/**
 * Merchant's AI conversation report (F003 of the AI-conversation billing
 * epic). Contracts: core-documentation, epic facturacion-conversaciones-ia,
 * design/03-api-design.md §4. Every endpoint takes the merchant from the
 * session.
 */

export enum Granularity {
  DAY = 'DAY',
  MONTH = 'MONTH',
}

export enum AIConversationReportErrorCode {
  INVALID_RANGE = 'INVALID_RANGE',
  INVALID_GRANULARITY = 'INVALID_GRANULARITY',
  NOT_AVAILABLE = 'AI_REPORT_NOT_AVAILABLE',
  FORBIDDEN = 'AI_REPORT_FORBIDDEN',
  FAILED = 'AI_REPORT_FAILED',
}

export interface AIConversationKpis {
  conversations: number;
  /** USD */
  amount: number;
  /** USD per conversation in effect today, whatever the queried period */
  currentRate: number;
}

export interface AIConversationPoint {
  granularity: Granularity;
  /** `YYYY-MM-DD` for DAY, `YYYY-MM` for MONTH, in the merchant's timezone */
  period: string;
  conversations: number;
  /** USD */
  amount: number;
  /** true for a settled (closed and billed) month */
  settled: boolean;
}

export interface AIConversationReport {
  kpis: AIConversationKpis;
  series: AIConversationPoint[];
}

export interface AIConversationReportParams {
  /** `YYYY-MM-DD` */
  from: string;
  /** `YYYY-MM-DD` */
  to: string;
  assistantIds?: string[];
  granularity: Granularity;
}

export interface AIAssistantOption {
  id: string;
  name: string;
}

export interface AIConversationReportAvailability {
  available: boolean;
}

export interface CreateAIConversationReportRequest {
  emails: string[];
  /** Email language: ignored by the backend (always Spanish), sent as `es` */
  locate: string;
  nameFile: string;
  filters: {
    from: string;
    to: string;
    assistantIds?: string[];
  };
}

export interface CreateAIConversationReportResponse {
  code: number;
  message: string;
}

/** `errorCode` of a report error (the SDK spreads the 4xx body into `data`) */
export const getAIReportErrorCode = (
  error: unknown
): AIConversationReportErrorCode | undefined =>
  isQueryErrorResult(error) ? error.data?.errorCode : undefined;

/**
 * botbackoffice answers 403 without `errorCode` to a request with no session
 * or an expired one (03-api-design §4), where the shared base query only
 * handles 401: send the user to log in again.
 */
export const handleReportError = (error: unknown) => {
  if (
    isQueryErrorResult(error) &&
    error.status === 403 &&
    !error.data?.errorCode &&
    !isPublicRoute()
  ) {
    window.location.href = '/login';
  }
  return { error: error as SerializedError };
};

const aiConversationReportApi = createApi({
  reducerPath: 'aiConversationReportApi',
  baseQuery: b2chatBaseQuery,
  tagTypes: ['aiConversationReport'],
  endpoints: ({ query, mutation }) => ({
    getAIConversationReport: query<
      AIConversationReport,
      AIConversationReportParams
    >({
      providesTags: ['aiConversationReport'],
      query: ({ from, to, granularity, assistantIds }) =>
        apiResource(
          B2ChatClient.resources.aiConversationReport.actions
            .getAIConversationReport,
          {
            params: {
              from,
              to,
              granularity,
              // Comma-separated is accepted, like the repeated parameter
              ...(assistantIds?.length
                ? { assistantIds: assistantIds.join(',') }
                : {}),
            },
            transformError: handleReportError,
          }
        ),
    }),
    getAIConversationReportAssistants: query<
      AIAssistantOption[],
      { from: string; to: string }
    >({
      query: ({ from, to }) =>
        apiResource(
          B2ChatClient.resources.aiConversationReport.actions
            .getAIConversationReportAssistants,
          { params: { from, to }, transformError: handleReportError }
        ),
      transformResponse: (response: { assistants: AIAssistantOption[] }) =>
        response.assistants,
    }),
    getAIConversationReportAvailability: query<
      AIConversationReportAvailability,
      void
    >({
      query: () =>
        apiResource(
          B2ChatClient.resources.aiConversationReport.actions
            .getAIConversationReportAvailability,
          { transformError: handleReportError }
        ),
      // Hidden until billing starts (ENG-2206): before the first billable day
      // there is nothing to show, so the menu and the page treat it as closed
      transformResponse: ({ available }: AIConversationReportAvailability) => ({
        available:
          available &&
          dayjs().format(ISO_DAY_FORMAT) >= AI_CONVERSATIONS_FIRST_DAY,
      }),
    }),
    createAIConversationReport: mutation<
      CreateAIConversationReportResponse,
      CreateAIConversationReportRequest
    >({
      query: data =>
        apiResource(
          B2ChatClientBroadcast.resources.createAIConversationReport.actions
            .report,
          { data, transformError: handleReportError }
        ),
    }),
  }),
});

export const {
  useGetAIConversationReportQuery,
  useGetAIConversationReportAssistantsQuery,
  useGetAIConversationReportAvailabilityQuery,
  useCreateAIConversationReportMutation,
} = aiConversationReportApi;

export default aiConversationReportApi;
